import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { ShippingMethodStatus } from '@invenco/common-domain/enums/ShippingMethod';
import type { ShippingMethodDTO } from '@invenco/shipping-interface';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import { useWhileDialogOpen } from '@/components/dialog-with-context';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SelectFormField } from '@/components/forms/select-form-field';
import { SwitchFormField } from '@/components/forms/switch-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { Result } from '@/shared/helpers/Result';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { shippingMethodStatusTitle } from '@/shared/title-maps';

const formSchema = z.object({
  // Inherited from the parent warehouse method and not editable here — shown read-only for
  // reference only, never submitted.
  name: z.string().optional(),
  status: z.enum(ShippingMethodStatus),
  ratesCarrierAccountId: z.string().min(1, 'Please select rates carrier account'),
  bookingCarrierAccountId: z.string().min(1, 'Please select booking carrier account'),
  dynamicallyPriced: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

/** The editable subset of a managed shipping method. */
export type ManagedShippingMethodEditFormValues = {
  status: ShippingMethodStatus;
  ratesCarrierAccountId: string;
  bookingCarrierAccountId: string;
  dynamicallyPriced: boolean;
};

type Props = {
  accountId: string;
  /** The managed shipping method to prefill and edit. */
  shippingMethod?: ShippingMethodDTO;
  /**
   * Receives the validated editable values. The parent owns the update mutation,
   * so this component stays agnostic about persistence.
   */
  onSubmit: (values: ManagedShippingMethodEditFormValues) => Promise<Result>;
};

export function ManagedShippingMethodEditDialogContent({
  accountId,
  shippingMethod,
  onSubmit,
}: Props) {
  const { shippingGateway } = useGateways();

  // Freeze the DTO while the dialog is closing so the parent clearing its edit target
  // (or a row click that lands mid-animation) can't swap the form's values as the panel
  // slides out. Once closed and reopened with a new row, the new DTO flows through.
  const stableShippingMethod = useWhileDialogOpen(shippingMethod);

  const defaultValues = useMemo<FormValues>(
    () => ({
      name: stableShippingMethod?.name ?? '',
      // Seeded with ACTIVE so the form is well-typed before a row is selected; the prefilled
      // value always wins via `form.reset(defaultValues)` once a DTO is captured.
      status: stableShippingMethod?.status ?? ShippingMethodStatus.ACTIVE,
      ratesCarrierAccountId: stableShippingMethod?.ratesCarrierAccountId ?? '',
      bookingCarrierAccountId: stableShippingMethod?.bookingCarrierAccountId ?? '',
      dynamicallyPriced: stableShippingMethod?.dynamicallyPriced ?? false,
    }),
    [
      stableShippingMethod?.name,
      stableShippingMethod?.status,
      stableShippingMethod?.ratesCarrierAccountId,
      stableShippingMethod?.bookingCarrierAccountId,
      stableShippingMethod?.dynamicallyPriced,
    ],
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const { data: carrierServices = [], isLoading: isLoadingCarrierServices } = useQuery({
    queryKey: ['carrierServices'],
    queryFn: ({ signal }) => shippingGateway.getCarrierServices(undefined, { signal }),
    select: ({ items }) => items,
  });

  // Carrier service is inherited from the parent warehouse method and not editable here, so the
  // carrier is fixed — resolve its id once to scope the carrier-account dropdowns to that carrier.
  const carrierId = useMemo(
    () =>
      carrierServices.find(
        (carrierService) => carrierService.id === stableShippingMethod?.carrierServiceId,
      )?.carrierId,
    [carrierServices, stableShippingMethod?.carrierServiceId],
  );

  const { data: carrierAccounts = [], isLoading: isLoadingCarrierAccounts } = useQueryWithInput({
    parentKey: 'carrierAccounts',
    input: {
      accountId,
      carrierIds: carrierId ? [carrierId] : undefined,
    },
    query: (input, { signal }) => shippingGateway.getCarrierAccounts(input, { signal }),
    select: ({ items }) => items,
    // Wait for carrierId so we never briefly fetch the unscoped (all carriers) list and
    // populate the dropdowns with accounts that don't match the locked carrier service.
    enabled: Boolean(accountId) && Boolean(carrierId),
  });

  const isLoadingAccounts = isLoadingCarrierServices || isLoadingCarrierAccounts;
  const hasCarrierAccounts = carrierAccounts.length > 0;
  const carrierAccountPlaceholder = isLoadingAccounts
    ? 'Loading carrier accounts...'
    : hasCarrierAccounts
      ? 'Select a carrier account'
      : 'No carrier accounts available';

  return (
    <FormDialogContent
      form={form}
      side="right"
      submitLabel="Save"
      onSubmit={async (data) =>
        onSubmit({
          status: data.status,
          ratesCarrierAccountId: data.ratesCarrierAccountId,
          bookingCarrierAccountId: data.bookingCarrierAccountId,
          dynamicallyPriced: data.dynamicallyPriced,
        })
      }
    >
      <DialogHeader>
        <DialogTitle>Edit managed shipping method</DialogTitle>
        <DialogDescription>Update the settings for this managed shipping method.</DialogDescription>
      </DialogHeader>
      <DialogBody>
        <TextFormField control={form.control} name="name" label="Name" disabled />

        <SelectFormField
          control={form.control}
          name="status"
          label="Status"
          placeholder="Select status"
          className="w-full"
        >
          {Object.values(ShippingMethodStatus).map((status) => (
            <SelectItem key={status} value={status}>
              {shippingMethodStatusTitle[status]}
            </SelectItem>
          ))}
        </SelectFormField>

        <SelectFormField
          control={form.control}
          name="ratesCarrierAccountId"
          label="Rates carrier account"
          placeholder={carrierAccountPlaceholder}
          disabled={isLoadingAccounts || !hasCarrierAccounts}
          className="w-full"
        >
          {carrierAccounts.map((carrierAccount) => (
            <SelectItem key={carrierAccount.id} value={carrierAccount.id}>
              {carrierAccount.name}
            </SelectItem>
          ))}
        </SelectFormField>

        <SelectFormField
          control={form.control}
          name="bookingCarrierAccountId"
          label="Booking carrier account"
          placeholder={carrierAccountPlaceholder}
          disabled={isLoadingAccounts || !hasCarrierAccounts}
          className="w-full"
        >
          {carrierAccounts.map((carrierAccount) => (
            <SelectItem key={carrierAccount.id} value={carrierAccount.id}>
              {carrierAccount.name}
            </SelectItem>
          ))}
        </SelectFormField>

        <SwitchFormField
          control={form.control}
          name="dynamicallyPriced"
          label="Dynamically priced"
        />
      </DialogBody>
    </FormDialogContent>
  );
}
